import { ISOLATED_INCOME_MULT } from '../../data/signalLinks.js';
import type { MatchState } from '../State.js';
import { SECONDS_PER_TICK } from '../State.js';
import type { PlayerId } from '../ids.js';

// Income formula per docs/02_GAME_DESIGN.md §4 and the V0.2 prompt:
//   per_second(player) = 2 (base) + Σ generatesSignal of held nodes + (2 if all 3 held)
// The cached per-second rate lives on PlayerState for the HUD; the per-tick share
// (rate / 20) is added to signal each tick.
//
// V1.5 Underdog Surge (docs/02_GAME_DESIGN.md §7 / §18, docs/03_MVP_SCOPE.md V1.5
// IN list): when a player's signalScore trails the leader's by at least
// UNDERDOG_SURGE_GAP_MIN, multiply their post-relay income by
// (1 + UNDERDOG_SURGE_BONUS) up to a soft cap at the leader's income/sec. Surge
// is a SOFT comeback lever — by default it never lets the trailing player
// out-earn the leader (Rule 17, docs/02_GAME_DESIGN.md §18 "artificial
// backstop").
//
// Metric choice: signalScore. PlayerState already tracks it (cumulative income
// since match start), so a meaningful gap directly reflects "you've held more
// relays for longer." Using signalScore avoids the relay-count edge case where
// a player who *just* flipped a relay would instantly satisfy a relay-only
// metric without the cumulative pressure of being behind.
//
// V1.9 tuning — CrazyGames stats showed avg playtime dropping to 2m34s with
// the comeback never landing in time. Two changes:
//   1. Gap threshold lowered 250 → 120. Base trickle is 2 S/sec; the new
//      threshold is roughly 60 s of pure relay differential (vs. the previous
//      150 s), so the surge engages while the player still cares.
//   2. Cap raised when own Core HP is critical. If the trailing player's Core
//      is below CORE_HP_UNCAP_RATIO, the leader-rate cap is removed — the
//      surge becomes a real "last stand" mechanic instead of a token boost.
//      This only fires when the player is already a couple of relay losses
//      from defeat, so it can't break the "leader doesn't get out-earned in
//      the normal mid-game" invariant.
export const UNDERDOG_SURGE_BONUS = 0.25;
export const UNDERDOG_SURGE_GAP_MIN = 120;
// V1.9 — "last stand" uncap threshold. Below 30% Core HP the soft cap is
// disabled so the trailing player can actually fund a defensive squad
// rush instead of slowly drowning at the cap. Per V1.9 playtime-rescue
// goal: a player who feels doomed at 2:00 leaves at 2:00 — the surge
// must matter at that exact moment to keep them in the chair.
export const UNDERDOG_SURGE_LAST_STAND_HP_RATIO = 0.3;

export function economySystem(state: MatchState): void {
  const baseIncome: [number, number] = [
    computeBaseIncome(state, 0),
    computeBaseIncome(state, 1),
  ];
  const surge: [boolean, boolean] = computeSurge(state);
  // Telemetry — count rising-edge surge activations (transitions from false
  // to true) before we overwrite the prior flag. Accumulate per-tick seconds
  // any time the flag is currently true.
  for (const id of [0, 1] as const) {
    if (surge[id] && !state.underdogSurgeActive[id]) {
      state.stats.underdogSurgeActivationsByPlayer[id] += 1;
    }
    if (surge[id]) {
      state.stats.underdogSurgeSecondsByPlayer[id] += SECONDS_PER_TICK;
    }
  }
  state.underdogSurgeActive = surge;

  for (const player of state.players) {
    const baseRate = baseIncome[player.id];
    const leaderRate = player.id === 0 ? baseIncome[1] : baseIncome[0];
    let rate = baseRate;
    if (surge[player.id]) {
      const lastStand = isCoreInLastStand(state, player.id);
      const targetRate = baseRate * (1 + UNDERDOG_SURGE_BONUS);
      // V1.9 — last stand uncaps the surge so a near-dead trailing
      // player can actually fund a comeback push. Outside last stand
      // we keep the leader-rate cap so the V1.5 "never out-earn the
      // leader in the normal mid-game" invariant holds.
      const surged = lastStand ? targetRate : Math.min(targetRate, leaderRate);
      rate = Math.max(baseRate, surged);
    }
    player.signalIncomePerSec = rate;
    if (rate > player.peakIncomePerSec) player.peakIncomePerSec = rate;
    const delta = rate * SECONDS_PER_TICK;
    player.signal += delta;
    player.signalScore += delta;
  }
}

function isCoreInLastStand(state: MatchState, playerId: PlayerId): boolean {
  for (const core of Object.values(state.cores)) {
    if (core.ownerId !== playerId) continue;
    if (core.maxHp <= 0) return false;
    return core.hp / core.maxHp < UNDERDOG_SURGE_LAST_STAND_HP_RATIO;
  }
  return false;
}

function computeBaseIncome(state: MatchState, playerId: PlayerId): number {
  const baseTrickle = 2;
  let held = 0;
  let nodeIncome = 0;
  let totalNodes = 0;
  for (const node of Object.values(state.controlNodes)) {
    totalNodes += 1;
    if (node.ownerId === playerId) {
      held += 1;
      // V1.5 Option A: an isolated relay (ownerId set, isConnected=false)
      // emits ISOLATED_INCOME_MULT × generatesSignal. When signal links are
      // disabled (V0.x), every owned relay reads isConnected=true so the
      // multiplier is a no-op and the V0.x income formula is preserved.
      const linkMult = node.isConnected ? 1 : ISOLATED_INCOME_MULT;
      nodeIncome += node.generatesSignal * linkMult;
    }
  }
  const dominionBonus = totalNodes > 0 && held === totalNodes ? 2 : 0;
  return baseTrickle + nodeIncome + dominionBonus;
}

function computeSurge(state: MatchState): [boolean, boolean] {
  const score0 = state.players[0].signalScore;
  const score1 = state.players[1].signalScore;
  const gap = Math.abs(score0 - score1);
  if (gap < UNDERDOG_SURGE_GAP_MIN) return [false, false];
  if (score0 < score1) return [true, false];
  return [false, true];
}
